import { useEffect, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { Seg } from '../../ui/Seg';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioSheet } from '../studio/StudioSheet';
import { OWN_STYLE as OWN, type CoverStyle } from './cover-policy';

const HOLD_MS = 450;

/** Пример звучания стиля: одна дорожка на экран; новый пример гасит прежний (APP-SPEC-5 §9). */
function usePreview() {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState('');
  useEffect(
    () => () => {
      audio.current?.pause();
    },
    [],
  );
  const play = (style: CoverStyle) => {
    audio.current?.pause();
    if (playing === style.id || !style.previewUrl) {
      setPlaying('');
      return;
    }
    const next = new Audio(style.previewUrl);
    audio.current = next;
    next.onended = () => setPlaying('');
    void next.play().then(
      () => setPlaying(style.id),
      () => setPlaying(''),
    );
  };
  const stop = () => {
    audio.current?.pause();
    setPlaying('');
  };
  return { playing, play, stop };
}

function Card({
  style,
  own,
  on,
  playing,
  disabled,
  onPick,
  onHold,
}: {
  style?: CoverStyle;
  own?: boolean;
  on: boolean;
  playing: boolean;
  disabled: boolean;
  onPick(): void;
  onHold(): void;
}) {
  const timer = useRef(0);
  const held = useRef(false);
  return (
    <button
      type="button"
      className={`cv-card${on ? ' on' : ''}${playing ? ' spin' : ''}${own ? ' own' : ''}`}
      aria-pressed={on}
      disabled={disabled}
      style={style?.color ? { ['--cv' as string]: style.color } : undefined}
      onPointerDown={() => {
        held.current = false;
        clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          held.current = true;
          onHold();
        }, HOLD_MS);
      }}
      onPointerUp={() => clearTimeout(timer.current)}
      onPointerLeave={() => clearTimeout(timer.current)}
      onContextMenu={event => {
        event.preventDefault();
        onHold();
      }}
      onClick={() => {
        if (held.current) {
          held.current = false;
          return;
        }
        onPick();
      }}
    >
      <span className="cv-vinyl" aria-hidden="true">
        {own ? <StudioIcon kind="edit" /> : <StudioIcon kind="music" />}
      </span>
      {style?.hit && <span className="cv-badge">{uiText('Хит')}</span>}
      {!style?.hit && style?.isNew && <span className="cv-badge">{uiText('Новое')}</span>}
      {on && style?.previewUrl && <span className="cv-play" aria-hidden="true"><StudioIcon kind="play" /></span>}
      <b>{own ? uiText('Свой стиль') : style!.name}</b>
    </button>
  );
}

/** Лента «Стиль · Все N» и лист «Все стили» кавера (APP-SPEC-5 §1.2, §9): первой — «Свой стиль»,
    восемь самых популярных и выбранный; удержание — пример звучания; лист — вкладки, темы, поиск. */
export function CoverStyles({
  styles,
  themes,
  value,
  own,
  ownAvailable,
  disabled,
  onPick,
}: {
  styles: CoverStyle[];
  themes: string[];
  value: string;
  own: boolean;
  ownAvailable: boolean;
  disabled: boolean;
  onPick(id: string | null): void;
}) {
  const preview = usePreview();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'pop' | 'new'>('all');
  const [theme, setTheme] = useState('');
  const [query, setQuery] = useState('');
  const [hintSeen, setHintSeen] = useState(false);
  const top = [...styles].sort((a, b) => b.popularity - a.popularity).slice(0, 8);
  const picked = styles.find(s => s.id === value);
  const rail = picked && !top.includes(picked) ? [...top, picked] : top;
  const pick = (style: CoverStyle) => {
    if (style.id === value && !own) preview.play(style);
    else onPick(style.id);
  };
  const hold = (style: CoverStyle) => {
    setHintSeen(true);
    preview.play(style);
  };
  const q = query.trim().toLocaleLowerCase().replace(/ё/g, 'е');
  const grid = styles
    .filter(s => tab !== 'new' || s.isNew)
    .filter(s => !theme || s.themes.includes(theme))
    .filter(s => !q || `${s.name} ${s.description}`.toLocaleLowerCase().replace(/ё/g, 'е').includes(q))
    .sort((a, b) => (tab === 'pop' ? b.popularity - a.popularity : 0));
  return (
    <div className="cv-styles rl-w">
      <div className="glbl">
        {uiText('Стиль')}
        <button type="button" className="pp-ed" disabled={disabled} onClick={() => setOpen(true)}>
          {uiFormat('Все {0}', styles.length)}
          <StudioIcon kind="chev" />
        </button>
      </div>
      <div className="cv-rail" role="group" aria-label={uiText('Стиль')}>
        {ownAvailable && <Card own on={own} playing={false} disabled={disabled} onPick={() => onPick(OWN)} onHold={() => onPick(OWN)} />}
        {rail.map(style => (
          <Card key={style.id} style={style} on={!own && style.id === value} playing={preview.playing === style.id} disabled={disabled} onPick={() => pick(style)} onHold={() => hold(style)} />
        ))}
      </div>
      {open && (
        <StudioSheet
          title="Все стили"
          onClose={() => {
            preview.stop();
            setOpen(false);
          }}
        >
          <h2>{uiText('Все стили')}</h2>
          {styles.length > 12 && (
            <label className="search">
              <input type="search" value={query} placeholder={uiText('Найти стиль')} aria-label={uiText('Найти стиль')} onChange={event => setQuery(event.target.value)} />
            </label>
          )}
          <Seg
            className="sm"
            label={uiText('Вкладки стилей')}
            items={[
              { key: 'all', label: uiText('Все') },
              { key: 'pop', label: uiText('Популярные') },
              { key: 'new', label: uiText('Новые'), disabled: !styles.some(s => s.isNew) },
            ]}
            value={tab}
            onChange={value => setTab(value as 'all' | 'pop' | 'new')}
          />
          {themes.length > 0 && (
            <div className="cv-themes" role="group" aria-label={uiText('Темы')}>
              {themes.map(item => (
                <button key={item} type="button" className={`chip${theme === item ? ' on' : ''}`} aria-pressed={theme === item} onClick={() => setTheme(current => (current === item ? '' : item))}>
                  {item}
                </button>
              ))}
            </div>
          )}
          <div className="cv-grid">
            {ownAvailable && !q && !theme && tab === 'all' && (
              <Card
                own
                on={own}
                playing={false}
                disabled={disabled}
                onPick={() => {
                  onPick(OWN);
                  setOpen(false);
                }}
                onHold={() => onPick(OWN)}
              />
            )}
            {grid.map(style => (
              <Card
                key={style.id}
                style={style}
                on={!own && style.id === value}
                playing={preview.playing === style.id}
                disabled={disabled}
                onPick={() => {
                  onPick(style.id);
                  preview.stop();
                  setOpen(false);
                }}
                onHold={() => hold(style)}
              />
            ))}
          </div>
          {!grid.length && (
            <p role="status">
              {uiText('Ничего не нашлось')} · {uiText('Попробуйте другое слово или сбросьте тему')}
            </p>
          )}
          {!hintSeen && <p className="hint">{uiText('Нажмите, чтобы выбрать · Удерживайте, чтобы послушать пример')}</p>}
        </StudioSheet>
      )}
    </div>
  );
}
